import React, { useCallback, useMemo } from 'react';
import ExportPanel from '../../ui/ExportPanel.jsx';
import { downloadDecryptedHls } from '../../export/hlsDecrypt.js';
import { downloadHlsOriginals } from '../../export/hlsOriginals.js';
import { tsToMp4 } from '../../export/mp4Export.js';
import { toHex } from '../../crypto/activityCrypto.js';
import { journalText } from '../sessionJournal.js';

const csvCell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
const toCsv = (header, rows) => `\uFEFF${[header, ...rows].map(row => row.map(csvCell).join(';')).join('\r\n')}\r\n`;
const dateFr = value => (value ? new Date(value).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' }) : '');

/** Fiche de la session : ce que le serveur sait d'elle (jamais de clé). */
function sessionSheet(details) {
    const keep = ['sessionId', 'sessionType', 'status', 'mediaType', 'alertTriggerMethod', 'createdAt', 'updatedAt', 'deadline',
        'deviceModel', 'osName', 'osVersion', 'appVersion', 'batteryLevel', 'networkType', 'locationLat', 'locationLon', 'cryptoVersion'];
    return Object.fromEntries(keep.filter(key => details?.[key] !== undefined && details?.[key] !== null).map(key => [key, details[key]]));
}

/**
 * Exports d'une relecture de session (dashboard et lien police) : média
 * d'origine déchiffré (TS, sans perte), copie MP4 redressée, et dossier
 * complet avec segments chiffrés d'origine, registre et clés de lecture.
 *
 * @param loaded    résultat de loadAlertSession
 * @param registry  { url, headers } : registre de preuve (admin ou lien police)
 * @param exportLog { url, headers } : inscription de l'empreinte du dossier à l'audit
 */
export default function SessionReplayExports({ loaded, source, registry, exportLog, people = null, witnesses = null, exportedFrom = '' }) {
    const { headers, mediaKey } = loaded;
    // Journal du dossier : même texte qu'à l'écran (e-mail et position des intervenants).
    const exportBundle = useMemo(() => ({
        ...loaded.bundle,
        events: (loaded.bundle?.events || []).map(event => ({ ...event, label: journalText(event, people) })),
    }), [loaded, people]);
    const fetchBlob = useCallback(async url => (await fetch(url, { headers })).blob(), [headers]);

    const mediaExporters = useMemo(() => (loaded.bundle?.tracks || []).flatMap(track => track.media.flatMap(media => {
        const name = media.type === 'video' ? 'Vidéo' : 'Audio';
        const decrypt = onProgress => downloadDecryptedHls({ playlistUrl: media.playlistUrl, headers, key: mediaKey, onProgress });
        return [{
            id: `${media.id}-ts`,
            label: `${name} d’origine déchiffrée (TS, sans perte)`,
            fileName: `session-${media.type}.ts`,
            fetch: decrypt,
        }, {
            // Réencodée et redressée au quart de tour : pratique à lire, mais ce n'est pas la pièce d'origine.
            id: `${media.id}-mp4`,
            label: `${name} MP4${media.type === 'video' ? ' redressée' : ''} (copie de lecture)`,
            fileName: `session-${media.type}-lecture.mp4`,
            fetch: async onProgress => tsToMp4({ tsBlob: await decrypt(onProgress), onProgress }),
        }];
    })), [loaded, headers, mediaKey]);

    const evidence = useCallback(async () => {
        // Témoins (dashboard seulement) : consultation auditée par le fournisseur.
        const witnessList = witnesses ? await witnesses().catch(() => null) : null;
        const response = await fetch(registry.url, { headers: registry.headers });
        const registre = response.ok ? await response.json() : null;
        // Segments chiffrés tels que conservés : ce sont eux que le registre a enregistrés.
        const originals = [];
        for (const media of (loaded.bundle?.tracks || []).flatMap(track => track.media)) {
            originals.push(...await downloadHlsOriginals({ playlistUrl: media.playlistUrl, headers, folder: `medias/originaux/${media.type}` }));
        }
        const lastAlert = [...(loaded.journal || [])].reverse().find(entry => entry.color === '#D32F2F' && entry.kind === 'CONVERTSESSION');
        const extra = [
            { path: 'fiche-session.json', content: `${JSON.stringify(sessionSheet(loaded.details), null, 2)}\n`,
                description: 'Fiche de la session : identifiant, type, statut, dates, appareil, conservation' },
            { path: 'donnees/evenements-dechiffres.json', content: `${JSON.stringify({ initialState: loaded.initialState || null, events: (loaded.events || []).map(event => ({
                timestamp: event.timestamp, eventType: event.eventType, data: (() => { try { return JSON.parse(event.eventData); } catch (_) { return event.eventData; } })(),
            })) }, null, 2)}\n`, description: 'Événements de la session déchiffrés, forme technique d’origine (JSON)' },
        ];
        if (witnessList) {
            extra.push({
                path: 'temoins.csv',
                content: toCsv(['nom', 'email', 'type', 'reaction', 'prevenu_le', 'vu_le', 'derniere_mise_a_jour', 'derniere_position'],
                    witnessList.map(w => [w.displayName, w.email, w.kind, w.statusLabel, dateFr(w.notifiedAt), dateFr(w.seenAt), dateFr(w.lastUpdateAt),
                        w.lastPosition ? `${w.lastPosition.lat.toFixed(5)}, ${w.lastPosition.lng.toFixed(5)}` : ''])),
                description: 'Personnes prévenues (amis, communauté) : réaction, horaires, dernière position (confidentiel)',
            });
        }
        return {
            registre,
            context: {
                exportedFrom,
                witnessCount: witnessList ? witnessList.length : null,
                sheet: {
                    type: loaded.details?.sessionType || null,
                    trigger: lastAlert?.label || null,
                    mediaType: loaded.details?.mediaType || null,
                    retentionUntil: loaded.details?.deadline || null,
                },
            },
            files: [
                ...extra,
                ...originals,
                { path: 'preuve/originaux-chiffres.json', content: `${JSON.stringify(loaded.originals, null, 2)}\n`,
                    description: 'État initial et événements tels que conservés par le serveur (chiffrés)' },
                { path: 'preuve/cle-de-lecture.txt',
                    content: `Clé des événements (AES-128-GCM, format GCM1) : ${toHex(loaded.eventsKey)}\nClé des segments vidéo/audio (AES-128-CBC, HLS) : ${toHex(loaded.mediaKey)}\n`
                        + 'Document confidentiel : ces clés permettent de lire la session.\n',
                    description: 'Clés de lecture (confidentiel)' },
            ],
        };
    }, [registry, loaded, headers, witnesses, exportedFrom]);

    // L'empreinte du manifeste est inscrite au journal d'audit avant le téléchargement.
    const recordExport = useCallback(async ({ manifest, manifestSha256 }) => {
        const response = await fetch(exportLog.url, {
            method: 'POST',
            headers: { ...exportLog.headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ manifestSha256, files: manifest.fichiers.map(file => `${file.sha256}  ${file.octets}  ${file.chemin}`) }),
        });
        if (!response.ok) throw new Error("L'export n'a pas pu être inscrit au journal d'audit.");
    }, [exportLog]);

    return (
        <ExportPanel
            bundle={exportBundle}
            fetchBlob={fetchBlob}
            mediaExporters={mediaExporters}
            source={source}
            onExported={recordExport}
            evidence={evidence}
        />
    );
}
